import { useLayoutEffect } from 'react';
import { ArrowLeftIcon } from '@newcore/icons';
import { Button } from '@newcore/ui/buttons';
import { Card } from '@newcore/ui/card';
import { cn } from '@newcore/ui/cn';
import { FontWeights } from '@newcore/ui/enums/font-weights';
import { Sizes } from '@newcore/ui/enums/sizes';
import { TextSizes } from '@newcore/ui/enums/typography-text-sizes';
import { ButtonVariants } from '@newcore/ui/enums/variants';
import { Text } from '@newcore/ui/typography';
import newcoreAppHorizontalLogo from '@/assets/newcore-app-horizontal-logo.svg';
import newcoreTextLogo from '@/assets/newcore-app-text-logo.svg';
import { LoginButton } from '@/components/ui/login-button';
import { useBranding } from '@/hooks/use-branding';
import { useSignOn } from '@/hooks/use-sign-on';

const ESCALATION_SUBHEADING = 'Confirm your identity to continue';

interface LoginCardProps {
  className?: string;
  children: React.ReactNode;
  hasAlternatives?: boolean;
  onShowAlternatives?: () => void;
  username?: string;
  onBack?: () => void;
  subheading?: string;
  isEscalationFlow?: boolean;
}

export const LoginCard = ({
  className = '',
  children,
  hasAlternatives,
  onShowAlternatives,
  username,
  onBack,
  subheading,
  isEscalationFlow = false,
}: LoginCardProps) => {
  const { logoUrl, coverUrl } = useBranding();
  const { showCard } = useSignOn();

  useLayoutEffect(() => showCard(), [showCard]);
  const logoAbove = Boolean(logoUrl) && !coverUrl;
  const isBranded = Boolean(logoUrl) || Boolean(coverUrl);

  const header = (
    <header className='flex flex-col items-center gap-2'>
      <img
        className={logoUrl ? 'max-h-16 max-w-40 object-contain' : 'w-20'}
        src={logoUrl ?? newcoreAppHorizontalLogo}
        alt={logoUrl ? 'Organization logo' : 'NewCore Logo'}
      />
      {(isEscalationFlow || subheading) && (
        <Text size={TextSizes.SM} className='text-secondary'>
          {isEscalationFlow ? ESCALATION_SUBHEADING : subheading}
        </Text>
      )}
    </header>
  );

  return (
    <div className='relative flex w-full max-w-sm flex-col items-center gap-3'>
      <div className='relative w-full'>
        {logoAbove && (
          <div className='absolute inset-x-0 bottom-full mb-3 flex justify-center'>
            {header}
          </div>
        )}
        <Card
          className={cn(
            'w-full relative flex flex-col rounded-[0.75rem] border-secondary shadow-none',
            logoAbove ? 'px-8 py-10' : 'gap-6 p-6',
            className
          )}
        >
          {!logoAbove && (
            <div className='flex w-full flex-col items-center justify-center border-b border-secondary pb-6'>
              {header}
            </div>
          )}

          {username && (
            <div
              className='flex items-center gap-2 text-secondary'
              data-testid='username-header'
            >
              {onBack && (
                <Button
                  type='button'
                  variant={ButtonVariants.TERTIARY}
                  size={Sizes.SM}
                  className='px-4 text-primary [&_svg]:size-5'
                  onClick={onBack}
                  aria-label='Back to username'
                  data-testid='back-to-username-button'
                >
                  <ArrowLeftIcon className='text-primary' />
                </Button>
              )}
              <span className='wrap-anywhere' data-testid='username-display'>
                {username}
              </span>
            </div>
          )}

          {children}

          {hasAlternatives && onShowAlternatives && (
            <footer className='flex items-end justify-between'>
              <LoginButton
                type='button'
                variant={ButtonVariants.LINK_SECONDARY}
                onClick={onShowAlternatives}
              >
                Use a different verification method
              </LoginButton>
            </footer>
          )}
        </Card>
      </div>
      {isBranded && (
        <footer
          className={cn(
            'flex items-center gap-1 px-3 py-1',
            coverUrl && 'rounded-md bg-white/90 backdrop-blur-sm'
          )}
          data-testid='powered-by-newcore'
        >
          <Text
            size={TextSizes.XS}
            weight={FontWeights.REGULAR}
            className='text-secondary'
          >
            Powered by
          </Text>
          <img className='h-4' src={newcoreTextLogo} alt='NewCore' />
        </footer>
      )}
    </div>
  );
};
